import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Headphones, Gauge, MapPin, FileText, Info, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlanComparator } from "@/components/PlanComparator";
import { IMAGES, COMPANY } from "@/data/content";

const quickAccess = [
  { to: "/informacion", icon: MapPin, title: "Mapas de cobertura", desc: "Consulta si llegamos a tu zona" },
  { to: "/informacion#planes", icon: Wifi, title: "Planes y tarifas", desc: "Compara velocidades y precios" },
  { to: "/pqr", icon: FileText, title: "Radicar PQR", desc: "Peticiones, quejas y reclamos" },
  { to: "/medidor", icon: Gauge, title: "Medidor de velocidad", desc: "Prueba tu conexión ahora" },
];

const marquee = ["Fibra óptica FTTH", "WiFi 6 incluido", "Soporte 24/7", "Sin cláusulas de permanencia", "Instalación rápida", "Velocidad simétrica"];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-widest px-4 py-1.5 mb-6">
              <Zap className="h-3.5 w-3.5" /> Internet de fibra óptica
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              Conexión que <span className="text-primary">vuela</span>, atención que <span className="text-primary">cumple</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg leading-relaxed">
              En {COMPANY.name} llevamos internet de alta velocidad a tu hogar y negocio, con tecnología de fibra óptica y
              un equipo que responde cuando lo necesitas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/informacion">
                <Button size="lg" className="rounded-full h-13 px-8 font-semibold gap-2 text-base" data-testid="hero-info-btn">
                  <Info className="h-5 w-5" /> Información importante para usuarios
                </Button>
              </Link>
              <Link to="/medidor">
                <Button size="lg" variant="outline" className="rounded-full h-13 px-8 font-semibold gap-2 text-base" data-testid="hero-speed-btn">
                  <Gauge className="h-5 w-5" /> Medir velocidad
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
            <img src={IMAGES.hero} alt="Familia disfrutando internet Colnexx" className="relative rounded-3xl object-cover w-full h-[440px] shadow-2xl" />
            <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-2xl p-4 shadow-xl hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center"><Zap className="h-5 w-5 text-primary" /></div>
                <div>
                  <div className="font-display font-bold text-xl">Hasta 900 Mbps</div>
                  <div className="text-xs text-muted-foreground">Velocidad simétrica</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* marquee */}
        <div className="border-y border-border bg-secondary text-secondary-foreground py-4 overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee">
            {[...marquee, ...marquee].map((m, i) => (
              <span key={i} className="mx-8 font-display font-bold text-lg flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK ACCESS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-2">Accesos rápidos</h2>
        <p className="text-muted-foreground mb-10">Lo más importante, a un clic de distancia.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {quickAccess.map((q, i) => (
            <motion.div key={q.to} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
              <Link to={q.to} data-testid={`quick-access-${i}`} className="group block h-full rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-lg hover:border-primary/50 transition-all">
                <div className="h-12 w-12 rounded-xl bg-accent grid place-items-center mb-4 group-hover:bg-primary transition-colors">
                  <q.icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display font-bold text-lg mb-1">{q.title}</h3>
                <p className="text-sm text-muted-foreground">{q.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">Ver más <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-muted/50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: Zap, title: "Fibra de verdad", desc: "Tecnología FTTH directa a tu hogar para velocidad estable, sin importar la hora." },
            { icon: ShieldCheck, title: "Transparencia total", desc: "Publicamos indicadores de calidad, prácticas de tráfico y factores que afectan tu velocidad." },
            { icon: Headphones, title: "Atención cercana", desc: "Soporte 24/7 por WhatsApp, teléfono y web. Radica y sigue tus PQR en línea." },
          ].map((v) => (
            <div key={v.title} className="rounded-3xl bg-card border border-border p-8">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center mb-5"><v.icon className="h-7 w-7 text-primary" /></div>
              <h3 className="font-display text-xl font-bold mb-2">{v.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PLANS */}
      <section id="planes" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Planes y tarifas</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Elige el plan a tu medida</h2>
          <p className="text-muted-foreground mt-3">Precios claros, sin sorpresas. Todos incluyen instalación y WiFi.</p>
        </div>
        <PlanComparator />
        <p className="text-center text-xs text-muted-foreground mt-8 max-w-2xl mx-auto">
          Tarifas de referencia (contenido de ejemplo). Los valores incluyen IVA. Consulta términos y condiciones. Las velocidades corresponden al plan contratado y pueden variar por los factores descritos en Información importante.
        </p>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-8">
        <div className="rounded-3xl bg-secondary text-secondary-foreground p-10 sm:p-16 relative overflow-hidden grain">
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
          <div className="relative max-w-xl">
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">¿Necesitas ayuda o quieres radicar una PQR?</h2>
            <p className="mt-4 text-secondary-foreground/70 text-lg">Conoce tus derechos como usuario y los canales disponibles para tus peticiones, quejas, reclamos y recursos.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/pqr"><Button size="lg" className="rounded-full h-13 px-8 font-semibold">Radicar PQR</Button></Link>
              <Link to="/contacto"><Button size="lg" variant="outline" className="rounded-full h-13 px-8 font-semibold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">Canales de contacto</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
